import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Separator,
  Textarea,
} from '@commander/ui';
import { RiCloseLine, RiFileTextLine, RiMessageLine } from '@remixicon/react';
import { useEffect, useState } from 'react';
import type { SMSTemplate } from '../../types';
import { calculateSMSSegments } from '../../utils/formatters';
import { AiContentGenerator } from '../ai/AiContentGenerator';

interface SMSComposerProps {
  isOpen: boolean;
  onClose: () => void;
  onSend: (data: any) => void;
  template?: SMSTemplate;
  contactId?: string;
  contactName?: string;
  phoneNumber?: string;
}

export function SMSComposer({
  isOpen,
  onClose,
  onSend,
  template,
  contactId,
  contactName = '',
  phoneNumber = '',
}: SMSComposerProps) {
  const [phone, setPhone] = useState(phoneNumber);
  const [name, setName] = useState(contactName);
  const [message, setMessage] = useState('');
  const [characterCount, setCharacterCount] = useState(0);
  const [segments, setSegments] = useState(1);
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [isSending, setIsSending] = useState(false);

  // AI generation state
  const [isAiPopoverOpen, setIsAiPopoverOpen] = useState(false);

  // Update state when props change (e.g., when opening modal with different contact)
  useEffect(() => {
    setPhone(phoneNumber);
    setName(contactName);
  }, [phoneNumber, contactName]);

  useEffect(() => {
    if (template) {
      // Initialize variables from template
      const initialVars: Record<string, string> = {};
      template.variables.forEach((v) => {
        initialVars[v.key] = v.defaultValue || '';
      });
      setVariables(initialVars);

      // Apply variables to message
      let processedMessage = template.message;
      Object.entries(initialVars).forEach(([key, value]) => {
        processedMessage = processedMessage.replace(
          new RegExp(`{{${key}}}`, 'g'),
          value || `{{${key}}}`
        );
      });
      setMessage(processedMessage);
    }
  }, [template]);

  useEffect(() => {
    const count = message.length;
    setCharacterCount(count);
    const segs = calculateSMSSegments(message);
    setSegments(segs);
  }, [message]);

  const handleVariableChange = (key: string, value: string) => {
    const newVars = { ...variables, [key]: value };
    setVariables(newVars);

    if (template) {
      let processedMessage = template.message;
      Object.entries(newVars).forEach(([k, v]) => {
        processedMessage = processedMessage.replace(new RegExp(`{{${k}}}`, 'g'), v || `{{${k}}}`);
      });
      setMessage(processedMessage);
    }
  };

  const handleSend = async () => {
    const smsData = {
      phoneNumber: phone,
      contactName: name,
      contactId,
      message,
      segments,
      templateId: template?.id,
      variables: template ? variables : undefined,
    };

    setIsSending(true);
    try {
      await onSend(smsData);
      handleClose();
    } catch (_error) {
      // Error already logged and toasted by provider, just keep modal open
    } finally {
      setIsSending(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setPhone(phoneNumber || '');
    setName(contactName || '');
    setMessage('');
    setVariables({});
    // Reset AI state
    setIsAiPopoverOpen(false);
    onClose();
  };

  const handleAiContentGenerated = ({ body }: { subject?: string; body: any }) => {
    setMessage(body);
  };

  const getSegmentColor = () => {
    if (segments === 1) return 'text-green-600';
    if (segments === 2) return 'text-yellow-600';
    if (segments === 3) return 'text-orange-600';
    return 'text-red-600';
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RiMessageLine className="h-5 w-5" />
            Send SMS Message
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Recipient */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="sms-name">Contact Name</Label>
                <Input
                  id="sms-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="John Doe"
                  disabled={!!contactId}
                />
              </div>
              <div>
                <Label htmlFor="sms-phone">Phone Number</Label>
                <Input
                  id="sms-phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 (555) 123-4567"
                  disabled={!!contactId}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Template Variables */}
          {template && template.variables.length > 0 && (
            <>
              <div className="space-y-3">
                <Label className="flex items-center gap-2">
                  <RiFileTextLine className="h-4 w-4" />
                  Template Variables
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  {template.variables.map((variable) => (
                    <div key={variable.key}>
                      <Label htmlFor={`var-${variable.key}`} className="text-sm">
                        {variable.label}
                        {variable.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      <Input
                        id={`var-${variable.key}`}
                        type={variable.type === 'number' ? 'number' : 'text'}
                        value={variables[variable.key] || ''}
                        onChange={(e) => handleVariableChange(variable.key, e.target.value)}
                        placeholder={
                          variable.placeholder || `Enter ${variable.label.toLowerCase()}`
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* AI SMS Writer */}
          <div className="space-y-2">
            <Label>AI SMS Writer</Label>
            <AiContentGenerator
              type="sms"
              isOpen={isAiPopoverOpen}
              onOpenChange={setIsAiPopoverOpen}
              onContentGenerated={handleAiContentGenerated}
              buttonClassName="w-full"
              buttonVariant="outline"
              contactId={contactId}
              recipientName={name}
            />
          </div>

          <Separator />

          {/* Message Composition */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="sms-message">Message</Label>
              <div className="flex items-center gap-3 text-sm">
                <span className={getSegmentColor()}>{characterCount}/160</span>
                <Badge variant={segments > 1 ? 'secondary' : 'outline'}>
                  {segments} {segments === 1 ? 'segment' : 'segments'}
                </Badge>
              </div>
            </div>
            <Textarea
              id="sms-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message here..."
              className="min-h-[120px] font-mono text-sm"
              maxLength={1600} // 10 segments max
            />
            <div className="text-xs text-muted-foreground">
              {segments === 1
                ? 'Single SMS (160 characters)'
                : `Multi-part SMS (${segments} parts, 153 characters each)`}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSending}>
            <RiCloseLine className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSend} disabled={isSending || !phone || !message}>
            <RiMessageLine className="h-4 w-4" />
            <span className="ml-2">{isSending ? 'Sending...' : 'Send SMS'}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
